import type { Edge, Node } from '@xyflow/react';
import type { FlowchartNodeData, FlowchartEdgeData } from './flowchart-types';

export const FLOWCHART_DIRECTION_KEY = {
  Up: 'ArrowUp',
  Down: 'ArrowDown',
  Left: 'ArrowLeft',
  Right: 'ArrowRight',
} as const;

export type FlowchartDirectionKey = (typeof FLOWCHART_DIRECTION_KEY)[keyof typeof FLOWCHART_DIRECTION_KEY];

interface NodeCenter {
  x: number;
  y: number;
}

interface NavigationCandidate {
  id: string;
  primaryDelta: number;
  secondaryDelta: number;
  euclideanDistance: number;
}

interface NavigationOffset {
  deltaX: number;
  deltaY: number;
}

interface NavigationCandidateDeltas {
  primaryDelta: number;
  secondaryDelta: number;
}

const NAVIGATION_AXIS_EPSILON = 0.001;
const SECONDARY_NAVIGATION_WEIGHT = 0.35;

const PRIMARY_NAVIGATION_DELTA_BY_DIRECTION: Record<
  FlowchartDirectionKey,
  (offset: NavigationOffset) => number
> = {
  [FLOWCHART_DIRECTION_KEY.Up]: (offset) => -offset.deltaY,
  [FLOWCHART_DIRECTION_KEY.Down]: (offset) => offset.deltaY,
  [FLOWCHART_DIRECTION_KEY.Left]: (offset) => -offset.deltaX,
  [FLOWCHART_DIRECTION_KEY.Right]: (offset) => offset.deltaX,
};

const SECONDARY_NAVIGATION_DELTA_BY_DIRECTION: Record<
  FlowchartDirectionKey,
  (offset: NavigationOffset) => number
> = {
  [FLOWCHART_DIRECTION_KEY.Up]: (offset) => Math.abs(offset.deltaX),
  [FLOWCHART_DIRECTION_KEY.Down]: (offset) => Math.abs(offset.deltaX),
  [FLOWCHART_DIRECTION_KEY.Left]: (offset) => Math.abs(offset.deltaY),
  [FLOWCHART_DIRECTION_KEY.Right]: (offset) => Math.abs(offset.deltaY),
};

function getNodeCenter(node: Node<FlowchartNodeData>): NodeCenter {
  return {
    x: node.position.x + getNodeWidth(node) / 2,
    y: node.position.y + getNodeHeight(node) / 2,
  };
}

function getNodeWidth(node: Node<FlowchartNodeData>): number {
  return getNumericDimension(node.width)
    ?? getNumericDimension(node.style?.width)
    ?? 0;
}

function getNodeHeight(node: Node<FlowchartNodeData>): number {
  return getNumericDimension(node.height)
    ?? getNumericDimension(node.style?.height)
    ?? node.data.nodeHeight;
}

function getNumericDimension(value: unknown): number | null {
  return typeof value === 'number' ? value : null;
}

export function buildUndirectedNeighborMap(
  edges: Array<Edge<FlowchartEdgeData>>,
): Map<string, Set<string>> {
  return edges.reduce((neighborMap, edge) => {
    const sourceNeighbors = neighborMap.get(edge.source) ?? new Set<string>();
    sourceNeighbors.add(edge.target);
    neighborMap.set(edge.source, sourceNeighbors);

    const targetNeighbors = neighborMap.get(edge.target) ?? new Set<string>();
    targetNeighbors.add(edge.source);
    neighborMap.set(edge.target, targetNeighbors);

    return neighborMap;
  }, new Map<string, Set<string>>());
}

export function getNodeNeighborhood(input: {
  nodeId: string;
  neighborMap: Map<string, Set<string>>;
}): Set<string> {
  const directNeighbors = input.neighborMap.get(input.nodeId) ?? new Set<string>();
  return new Set<string>([input.nodeId, ...Array.from(directNeighbors)]);
}

function getNavigationCandidateDeltas(input: {
  offset: NavigationOffset;
  direction: FlowchartDirectionKey;
}): NavigationCandidateDeltas | null {
  const primaryDelta = PRIMARY_NAVIGATION_DELTA_BY_DIRECTION[input.direction](input.offset);
  if (primaryDelta <= NAVIGATION_AXIS_EPSILON) {
    return null;
  }

  return {
    primaryDelta,
    secondaryDelta: SECONDARY_NAVIGATION_DELTA_BY_DIRECTION[input.direction](input.offset),
  };
}

function getNavigationCandidate(input: {
  activeCenter: NodeCenter;
  candidateCenter: NodeCenter;
  candidateId: string;
  direction: FlowchartDirectionKey;
}): NavigationCandidate | null {
  const offset = {
    deltaX: input.candidateCenter.x - input.activeCenter.x,
    deltaY: input.candidateCenter.y - input.activeCenter.y,
  };
  const candidateDeltas = getNavigationCandidateDeltas({
    offset,
    direction: input.direction,
  });
  if (!candidateDeltas) {
    return null;
  }

  return {
    id: input.candidateId,
    primaryDelta: candidateDeltas.primaryDelta,
    secondaryDelta: candidateDeltas.secondaryDelta,
    euclideanDistance: Math.hypot(offset.deltaX, offset.deltaY),
  };
}

function sortNavigationCandidates(
  leftCandidate: NavigationCandidate,
  rightCandidate: NavigationCandidate,
): number {
  const leftScore = leftCandidate.primaryDelta + leftCandidate.secondaryDelta * SECONDARY_NAVIGATION_WEIGHT;
  const rightScore = rightCandidate.primaryDelta + rightCandidate.secondaryDelta * SECONDARY_NAVIGATION_WEIGHT;

  if (leftScore !== rightScore) {
    return leftScore - rightScore;
  }

  return leftCandidate.euclideanDistance - rightCandidate.euclideanDistance;
}

export function findDirectionalNodeId(input: {
  nodes: Array<Node<FlowchartNodeData>>;
  activeNodeId: string;
  direction: FlowchartDirectionKey;
}): string | null {
  const activeNode = input.nodes.find((node) => node.id === input.activeNodeId);
  if (!activeNode) {
    return null;
  }

  const activeCenter = getNodeCenter(activeNode);

  const bestCandidate = input.nodes
    .filter((node) => node.id !== input.activeNodeId)
    .map((node) => {
      const candidateCenter = getNodeCenter(node);
      return getNavigationCandidate({
        activeCenter,
        candidateCenter,
        candidateId: node.id,
        direction: input.direction,
      });
    })
    .filter((candidate): candidate is NavigationCandidate => candidate != null)
    .sort(sortNavigationCandidates)[0];

  return bestCandidate?.id ?? null;
}

export function findDirectionalNavigationTarget(input: {
  nodes: Array<Node<FlowchartNodeData>>;
  activeNodeId: string | null;
  direction: FlowchartDirectionKey;
}): string | null {
  const activeNodeId = input.activeNodeId ?? input.nodes[0]?.id ?? null;
  if (!activeNodeId) {
    return null;
  }

  return findDirectionalNodeId({
    nodes: input.nodes,
    activeNodeId,
    direction: input.direction,
  });
}
